import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import Address from "@mapmycustomers/shared/types/Address";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import {
  convertCoordinatesToGeoPoint,
  convertCoordinatesToLatLngLiteral,
  convertGeoPointToLatLngLiteral,
  convertLatLngToCoordinates,
} from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, Button } from "@mapmycustomers/ui";

import LocatedEntityAssociation from "@app/component/associations/LocatedEntityAssociation";
import ButtonLink from "@app/component/ButtonLink";
import AddressField from "@app/component/input/Address/AddressField";
import useMap from "@app/component/map/FeaturedMap/utils/useMap";
import AreaSearchMode from "@app/enum/AreaSearchMode";
import {
  geocodeLocation,
  setConfigurationVisibility,
  setLeadFinderQuery,
} from "@app/scene/map/store/leadFinder/actions";
import { LeadFinderQueryState } from "@app/scene/map/store/leadFinder/LeadFinderState";
import {
  getLeadFinderAddress,
  getLeadFinderEntity,
  getLeadFinderLocation,
  getLeadFinderMode,
} from "@app/scene/map/store/leadFinder/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { getPosition } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getFormattedAddressForUi } from "@app/util/formatters";
import locatedToAddress from "@app/util/locatedToAddress";

import SearchRadius from "../SearchRadius";

import styles from "./SearchArea.module.scss";

interface Props {
  address?: Address;
  classes?: {
    dropdown?: string;
    location?: string;
  };
  className?: string;
  compact?: boolean;
  entity?: EntityWithLocation;
  geocodeLocation: typeof geocodeLocation.request;
  location?: string;
  mode: AreaSearchMode;
  onChange?: (newMode?: AreaSearchMode) => void;
  position?: GeolocationPosition;
  query?: Partial<LeadFinderQueryState>;
  setConfigurationVisibility: typeof setConfigurationVisibility;
  setLeadFinderQuery: typeof setLeadFinderQuery;
  showEntityView: typeof showEntityView;
  text?: React.ReactNode;
}

const messages = defineMessages({
  addLocation: {
    id: "map.tool.leadFinder.popover.area.addLocation",
    defaultMessage: "Please add a location",
    description: "Address validation error",
  },
  addressLabel: {
    id: "map.tool.leadFinder.popover.area.addressLabel",
    defaultMessage: "Address",
    description: "Address label",
  },
  [AreaSearchMode.ADDRESS]: {
    id: "map.tool.leadFinder.popover.area.address",
    defaultMessage: "a specific address",
    description: "Your current location label",
  },
  [AreaSearchMode.CURRENT_MAP_VIEW_CENTER]: {
    id: "map.tool.leadFinder.popover.area.currentView",
    defaultMessage: "your current map view",
    description: "Your current map view label",
  },
  [AreaSearchMode.CURRENT_USER_LOCATION]: {
    id: "map.tool.leadFinder.popover.area.currentLocation",
    defaultMessage: "your current location",
    description: "Your current location label",
  },
  [AreaSearchMode.ENTITY]: {
    id: "map.tool.leadFinder.popover.area.entity",
    defaultMessage: "a specific Company or Person",
    description: "Your current location label",
  },
  around: {
    id: "map.tool.leadFinder.popover.area.around",
    defaultMessage: "around",
    description: "Around label",
  },
  changeRecord: {
    id: "map.tool.leadFinder.popover.area.changeRecord",
    defaultMessage: "Change Record",
    description: "Change Record button title",
  },
  locationError: {
    id: "map.tool.leadFinder.popover.area.locationError",
    defaultMessage: "Unable to detect location",
    description: "Unable to detect location label",
  },
  locationErrorHelp: {
    id: "map.tool.leadFinder.popover.area.locationError.help",
    defaultMessage: "Get help",
    description: "Unable to detect location - Get help button label",
  },
  search: {
    id: "map.tool.leadFinder.popover.area.text",
    defaultMessage: "Searching for leads",
    description: "Searching for leads label",
  },
  selectRecord: {
    id: "map.tool.leadFinder.popover.area.selectRecord",
    defaultMessage: "Select Record",
    description: "Select Record button title",
  },
});

const AVAILABLE_MODES = [
  AreaSearchMode.CURRENT_MAP_VIEW_CENTER,
  AreaSearchMode.CURRENT_USER_LOCATION,
  AreaSearchMode.ENTITY,
  AreaSearchMode.ADDRESS,
];

const CURRENT_MODES = [
  AreaSearchMode.CURRENT_MAP_VIEW_CENTER,
  AreaSearchMode.CURRENT_USER_LOCATION,
];

const SearchArea: React.FC<Props> = ({
  address,
  classes,
  className,
  compact = false,
  entity,
  geocodeLocation,
  location,
  mode,
  onChange,
  position,
  setConfigurationVisibility,
  setLeadFinderQuery,
  showEntityView,
  text,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const { map } = useMap();

  const [entityPopupVisible, showEntityPopup, hideEntityPopup] = useBoolean();

  const hasPosition =
    position?.coords?.longitude !== undefined && position?.coords?.latitude !== undefined;
  const showWarning = !hasPosition && mode === AreaSearchMode.CURRENT_USER_LOCATION;
  const showLocation = !!location && !showWarning && CURRENT_MODES.includes(mode);
  const hasAddress = !!address;

  useEffect(() => {
    switch (mode) {
      case AreaSearchMode.CURRENT_MAP_VIEW_CENTER:
        if (map) {
          const center = map.getCenter()!;
          const geoPoint = convertCoordinatesToGeoPoint(convertLatLngToCoordinates(center));

          if (geoPoint?.coordinates) {
            geocodeLocation({
              point: geoPoint.coordinates,
            });
          }
        }
        break;

      case AreaSearchMode.CURRENT_USER_LOCATION:
        if (hasPosition) {
          map?.panTo(convertCoordinatesToLatLngLiteral(position.coords));
          geocodeLocation({
            point: [position.coords.longitude, position.coords.latitude],
          });
        }
        break;
    }
  }, [geocodeLocation, hasPosition, map, mode, position]);

  const handleModeClick = useCallback(
    (value) => {
      const newMode: AreaSearchMode = value.key;

      if (newMode !== mode) {
        analytics.clicked(["Location", newMode]);

        if (newMode === AreaSearchMode.ENTITY) {
          showEntityPopup();
        } else {
          hideEntityPopup();
        }

        const query: LeadFinderQueryState = {
          entity: undefined,
          location: undefined,
          mode: newMode,
          point: undefined,
        };
        setLeadFinderQuery(query);

        // Lookup modes which trigger immediate request for a new results
        // Other modes will trigger refresh upon click of the corresponding action button
        if (CURRENT_MODES.includes(newMode)) {
          onChange?.(newMode);
        }
      }
    },
    [analytics, hideEntityPopup, mode, onChange, setLeadFinderQuery, showEntityPopup]
  );

  const handleAddressChange = useCallback(
    (value?: Address) => {
      setLeadFinderQuery({ address: value });
      if (value) {
        onChange?.();
      }
    },
    [onChange, setLeadFinderQuery]
  );

  const handleEntityViewClick = useCallback(() => {
    if (entity) {
      setConfigurationVisibility({ visible: false });

      showEntityView({
        entityId: entity.id,
        entityType: entity.entity,
      });
    }
  }, [entity, showEntityView, setConfigurationVisibility]);

  const handleSelectEntity = useCallback(
    (entity?: EntityWithLocation) => {
      let location;
      let point;

      if (entity) {
        location = getFormattedAddressForUi(locatedToAddress(entity));
        if (entity.geoPoint) {
          map?.panTo(convertGeoPointToLatLngLiteral(entity.geoPoint));
          point = entity.geoPoint.coordinates;
        }
      }

      setLeadFinderQuery({ entity, location, point });

      if (entity?.geoPoint?.coordinates) {
        onChange?.();
      }
    },
    [map, onChange, setLeadFinderQuery]
  );

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.selector}>
        {text ? text : intl.formatMessage(messages.search)}

        <SearchRadius classes={classes} />

        {intl.formatMessage(messages.around)}

        <Dropdown
          overlay={
            <Menu className={styles.menu} onClick={handleModeClick} selectedKeys={[mode]}>
              {AVAILABLE_MODES.map((key) => (
                <Menu.Item key={key}>{intl.formatMessage(messages[key])}</Menu.Item>
              ))}
            </Menu>
          }
          placement="bottomRight"
          trigger={["click"]}
        >
          <Button
            className={cn(styles.btn, classes?.dropdown)}
            rightIcon={<FontAwesomeIcon className={styles.arrow} icon={faAngleDown} />}
            type="text"
          >
            {intl.formatMessage(messages[mode])}
          </Button>
        </Dropdown>
      </div>

      {showWarning && (
        <Alert
          action={
            <ButtonLink
              className={styles.helpLink}
              href="https://support.mapmycustomers.com/hc/en-us/articles/360062391934-How-to-share-your-location-with-your-web-browser"
              target="_blank"
            >
              {intl.formatMessage(messages.locationErrorHelp)}
            </ButtonLink>
          }
          message={intl.formatMessage(messages.locationError)}
          showIcon
          type="warning"
        />
      )}

      {showLocation && (
        <div className={cn(styles.current, classes?.location)}>
          <span>{location}</span>
        </div>
      )}

      {mode === AreaSearchMode.ENTITY && (
        <div className={cn(styles.entity, { [styles.compact]: compact })}>
          {entity && (
            <div className={styles.entityInfo}>
              <div className={styles.entityName}>
                <ButtonLink onClick={handleEntityViewClick}>{entity.name}</ButtonLink>
              </div>

              <div className={styles.entityAddress}>{location}</div>
            </div>
          )}

          <div>
            <Button disabled={entityPopupVisible} onClick={showEntityPopup}>
              {intl.formatMessage(entity ? messages.changeRecord : messages.selectRecord)}
            </Button>
          </div>

          <LocatedEntityAssociation
            onHide={hideEntityPopup}
            onSelect={handleSelectEntity}
            selectedEntityId={entity?.id}
            visible={entityPopupVisible}
          />
        </div>
      )}

      {mode === AreaSearchMode.ADDRESS && (
        <div className={cn({ [styles.hasFeedback]: !hasAddress })}>
          <AddressField
            allowAutoCompleteClear
            label={intl.formatMessage(messages.addressLabel)}
            labelClassName={styles.addressLabel}
            onChange={handleAddressChange}
            value={address}
          />

          {!hasAddress && (
            <div className={styles.feedback}>{intl.formatMessage(messages.addLocation)}</div>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  address: getLeadFinderAddress(state),
  entity: getLeadFinderEntity(state),
  location: getLeadFinderLocation(state),
  mode: getLeadFinderMode(state),
  position: getPosition(state),
});

const mapDispatchToProps = {
  geocodeLocation: geocodeLocation.request,
  setConfigurationVisibility,
  setLeadFinderQuery,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchArea);
